import React, { Component } from "react";
import { DateRangePicker } from "react-date-range";
import { Button } from "reactstrap";
import { pt } from "react-date-range/dist/locale";
import { getLastMonthRange, getLastWeekRange, getThisMonthRange, getThisWeekRange, getTodayRange, getYesterdayRange, isSameRange } from "../utils/dateRanges";
import { normalizePeriodToDate, normalizeToTimestamp, isSameDay, formatPeriod, getDaysDiff } from "../utils/normalizeDateHelpers";

class GenericDatePicker extends Component {
    constructor(props) {
        super(props);

        const value = props.value || {};

        this.state = {
            compare: value.compare || false,
            showPanel: false,
            selection: value.selection
                ? normalizePeriodToDate({ startDate: value.selection.startDate, endDate: value.selection.endDate })
                : normalizePeriodToDate(),
            firstPeriod: value.firstPeriod ? normalizePeriodToDate(value.firstPeriod) : normalizePeriodToDate(),
            secondPeriod: value.secondPeriod ? normalizePeriodToDate(value.secondPeriod) : normalizePeriodToDate()
        };
    }

    componentDidMount() {
        if (this.props.emitOnMount) {
            this.emitChange();
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            nextState !== this.state ||
            nextProps.value !== this.props.value ||
            nextProps.showLabel !== this.props.showLabel ||
            nextProps.disableApply !== this.props.disableApply ||
            nextProps.disable !== this.props.disable
        );
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value && this.props.value) {
            const value = this.props.value;

            this.setState({
                compare: value.compare ?? this.state.compare,
                selection: value.selection
                    ? normalizePeriodToDate({ startDate: value.selection.startDate, endDate: value.selection.endDate })
                    : this.state.selection,
                firstPeriod: value.firstPeriod ? normalizePeriodToDate(value.firstPeriod) : this.state.firstPeriod,
                secondPeriod: value.secondPeriod ? normalizePeriodToDate(value.secondPeriod) : this.state.secondPeriod
            });
        }
    }

    handleShowPanel = () => {
        this.setState({ showPanel: !this.state.showPanel });

        if (this.props.onToggle) {
            this.props.onToggle();
        }
    };

    getFutureLimitDate = () => {
        const { disableMaxDate, maxDate = new Date() } = this.props;

        const today = new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate()
        );

        if (disableMaxDate) return null;

        const normalizedMax = new Date(
            maxDate.getFullYear(),
            maxDate.getMonth(),
            maxDate.getDate()
        );

        if (normalizedMax.getTime() > today.getTime()) {
            return normalizedMax;
        }

        return today;
    };

    getStaticRanges = () => {
        const { isCompare } = this.props;
        const { compare, selection } = this.state;

        const futureLimitDate = this.getFutureLimitDate();

        const ranges = [
            {
                id: "today",
                label: "Hoje",
                range: getTodayRange,
                isSelected: () => !compare && isSameRange(getTodayRange(), selection)
            },
            {
                id: "yesterday",
                label: "Ontem",
                range: getYesterdayRange,
                isSelected: () => !compare && isSameRange(getYesterdayRange(), selection)
            },
            {
                id: "thisWeek",
                label: "Esta Semana",
                range: () => getThisWeekRange(0, futureLimitDate),
                isSelected: () =>
                    !compare &&
                    isSameRange(getThisWeekRange(0, futureLimitDate), selection)
            },
            {
                id: "lastWeek",
                label: "Semana Passada",
                range: () => getLastWeekRange(),
                isSelected: () =>
                    !compare &&
                    isSameRange(getLastWeekRange(), selection)
            },
            {
                id: "thisMonth",
                label: "Este Mês",
                range: () => getThisMonthRange(futureLimitDate),
                isSelected: () => !compare && isSameRange(getThisMonthRange(futureLimitDate), selection)
            },
            {
                id: "lastMonth",
                label: "Mês Passado",
                range: getLastMonthRange,
                isSelected: () => !compare && isSameRange(getLastMonthRange(), selection)
            }
        ];

        if (isCompare) {
            ranges.unshift({
                label: compare ? "Desativar Comparativo" : "Ativar Comparativo",
                range: () => ({ compare: !compare }),
                isSelected: () => compare
            });
        }

        return ranges;
    };

    emitChange = () => {
        const { onChange, isCompare } = this.props;
        if (!onChange) return;

        const { compare, selection, firstPeriod, secondPeriod } = this.state;

        if (isCompare && compare) {
            onChange({
                firstPeriod: normalizeToTimestamp(firstPeriod.startDate, firstPeriod.endDate),
                secondPeriod: normalizeToTimestamp(secondPeriod.startDate, secondPeriod.endDate),
                compare
            });
            return;
        }

        onChange(
            normalizeToTimestamp(selection.startDate, selection.endDate)
        );
    };

    handleChange = (ranges) => {
        const toggleCompare =
            ranges?.selection?.compare ??
            ranges?.first_period?.compare ??
            ranges?.second_period?.compare;

        if (typeof toggleCompare === "boolean") {
            this.setState(
                { compare: toggleCompare },
                this.emitChange
            );
            return;
        }

        if (ranges.first_period || ranges.second_period) {
            this.setState(
                {
                    firstPeriod: ranges.first_period || this.state.firstPeriod,
                    secondPeriod: ranges.second_period || this.state.secondPeriod
                },
                this.emitChange
            );
            return;
        }

        if (ranges.selection) {
            this.setState(
                { selection: ranges.selection },
                this.emitChange
            );
        }
    };

    formatPeriod = (startDate, endDate) => {
        if (isSameDay(startDate, endDate)) {
            return formatDate(startDate);
        }

        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    };

    formatPeriodsLabel = () => {
        const { compare, selection, firstPeriod, secondPeriod } = this.state;

        if (!compare) {
            return (
                <span>
                    {formatPeriod(selection.startDate, selection.endDate)}
                </span>
            );
        }

        return (
            <span style={{ display: "flex", flexDirection: "column" }}>
                <span>
                    {formatPeriod(firstPeriod.startDate, firstPeriod.endDate)}
                </span>
                <span>
                    {formatPeriod(secondPeriod.startDate, secondPeriod.endDate)}
                </span>
            </span>
        );
    };

    getDaysDiff = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;

        const start = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate()
        );
        const end = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate()
        );

        const diffTime = end.getTime() - start.getTime();
        return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    isCompareRangeValid = () => {
        const { compare, firstPeriod, secondPeriod } = this.state;
        const { handleCompareRangeInvalid } = this.props;

        if (!compare) return true;

        const firstDays = getDaysDiff(firstPeriod.startDate, firstPeriod.endDate);

        const secondDays = getDaysDiff(secondPeriod.startDate, secondPeriod.endDate);

        if (firstDays !== secondDays && handleCompareRangeInvalid) {
            handleCompareRangeInvalid();
        }

        return firstDays === secondDays;
    };

    handleApply = () => {
        const { onApply } = this.props;

        this.setState({ showPanel: false });
        if (onApply) {
            onApply();
        }
    };

    render() {
        const {
            toggleText,
            applyText,
            disable = false,

            datepickerStyle = {},
            datepickerClass = "",
            toggleStyle = {},
            toggleClass = "",
            panelStyle = {},
            panelClass = "",
            applyStyle = {},
            applyClass = "",

            maxDate = new Date(),
            showLabel = false,
            disableApply = false,
            disableMaxDate = false,
        } = this.props;

        const { compare, showPanel, selection, firstPeriod, secondPeriod } = this.state;

        const isCompareValid = this.isCompareRangeValid();

        return (
            <>
                <div
                    className={`date-picker ${datepickerClass}`}
                    style={{
                        ...datepickerStyle,
                        opacity: disable ? 0.6 : 1,
                        pointerEvents: disable ? "none" : "auto",
                    }}
                >
                    <button
                        className={`date-picker__toggle ${toggleClass}`}
                        style={{
                            lineHeight: (compare && showLabel && !toggleText) ? "18px" : "25px",
                            padding: (compare && showLabel && !toggleText) ? "4px 8px" : "7px 8px",
                            ...toggleStyle
                        }}
                        onClick={this.handleShowPanel}
                    >
                        {toggleText ? (
                            <span>{toggleText}</span>
                        ) : (
                            <>
                                <i
                                    className={
                                        showPanel
                                            ? "fa fa-times fa-2x"
                                            : "fa fa-calendar fa-2x"
                                    }
                                />
                                {showLabel && (
                                    <span style={{ marginLeft: 5 }}>
                                        {this.formatPeriodsLabel()}
                                    </span>
                                )}
                            </>
                        )}
                    </button>
                </div>
                <div
                    className={
                        showPanel && !disable
                            ? `date-picker__panel ${panelClass}`
                            : "date-picker__panel--hidden"
                    }
                    style={{ ...panelStyle }}
                >
                    <DateRangePicker
                        locale={pt}
                        ranges={
                            compare
                                ? [
                                    { ...firstPeriod, key: "first_period" },
                                    { ...secondPeriod, key: "second_period" }
                                ]
                                : [{ ...selection, key: "selection" }]
                        }
                        maxDate={disableMaxDate ? undefined : maxDate}
                        onChange={this.handleChange}
                        staticRanges={this.getStaticRanges()}
                        inputRanges={[]}
                    />

                    <Button
                        className={`date-picker__apply ${applyClass}`}
                        style={applyStyle}
                        color="primary"
                        onClick={this.handleApply}
                        disabled={!isCompareValid || disableApply}
                    >
                        {applyText || "Aplicar"}
                    </Button>

                    {compare && !isCompareValid && (
                        <span
                            style={{
                                display: "block",
                                textAlign: "center",
                                marginTop: 8,
                                color: "#f2f2f2",
                                backgroundColor: "#dc3545",
                                fontSize: 14,
                                alignSelf: "center",
                                width: "max-content",
                                padding: "4px 8px",
                                borderRadius: ".5rem"
                            }}
                        >
                            Os períodos comparativos devem ter a mesma quantidade de dias.
                        </span>
                    )}
                </div>
            </>
        );
    }
}

export default GenericDatePicker;
